import { Alert, Box, Card, CardContent, CardHeader, Stack, Tab, Tabs, Typography } from '@mui/material';
import { type FC, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { url } from '../../appRouter/url';
import { type Geschaeftsbereich, geschaeftsbereiche } from '../../dtos';
import { trpc } from '../../trpc';
import { SearchInput } from '../Search/components/SearchInput';
import { TinyVACard } from './TinyVACard';

const inhouseVeranstaltungen = new Set<Geschaeftsbereich>([
  geschaeftsbereiche.IHC,
  geschaeftsbereiche.ILS,
  geschaeftsbereiche.CAS,
]);

const NoVAsFound: React.FC = () => (
  <Stack sx={{ height: '20vh' }} direction="row" justifyContent="center" alignItems="center">
    <Alert severity="info">Keine Veranstaltungen anstehend</Alert>
  </Stack>
);

export const DashboardPage: FC = () => {
  const veranstaltungenQuery = trpc.veranstaltung.getUpcomingVeranstaltungen.useQuery();
  const veranstaltungen = useMemo(() => veranstaltungenQuery.data ?? [], [veranstaltungenQuery.data]);
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState('all');

  const search = async (input: string): Promise<void> => {
    await navigate(url.search(input));
  };

  const getSelectedVeranstaltungen = useCallback(() => {
    switch (selectedTab) {
      case 'all':
        return veranstaltungen;
      case 'vc':
        return veranstaltungen.filter((va) => va.isOnline);
      case 'bl':
        return [];
      case 'inhouse':
        return veranstaltungen.filter((va) => inhouseVeranstaltungen.has(va.geschaeftsbereich));
      default:
        return veranstaltungen;
    }
  }, [selectedTab, veranstaltungen]);

  const selectedVAs = getSelectedVeranstaltungen();

  return (
    <Box display="flex" flexDirection="column" gap={2} padding={2} justifyContent="center">
      <Box display="flex" maxWidth="90vw" justifyContent="center" padding="10rem 0 10rem 0">
        <SearchInput search={search} initInput="" />
      </Box>
      <Box sx={{ pl: 10, pr: 10 }}>
        <Card sx={{ borderRadius: '0.5rem' }}>
          <CardHeader
            sx={{ paddingBottom: 0 }}
            component={Typography}
            variant="headingXxs"
            title="Anstehende Veranstaltungen"
          />
          <CardContent>
            {/* eslint-disable-next-line @typescript-eslint/no-unsafe-argument */}
            <Tabs value={selectedTab} onChange={(_, val) => setSelectedTab(val)} aria-label="Veranstaltungsarten Tabs">
              <Tab label="Alle" value="all" />
              <Tab label="VC" value="vc" />
              <Tab label="Blended Learning" value="bl" />
              <Tab label="Inhouse" value="inhouse" />
            </Tabs>
            <Stack direction="column" spacing={1} padding={1}>
              {selectedVAs.length === 0 ? (
                <NoVAsFound />
              ) : (
                selectedVAs.map((va) => <TinyVACard key={va.sapId} veranstaltung={va} />)
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
